"use client";

import {
  Button,
  cn,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@devhop/ui";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronsUpDown,
  FolderIcon,
  FolderOpenIcon,
} from "lucide-react";
import { useEffect, useMemo, useState, type MouseEvent } from "react";
import type { CascadingCategoryItem } from "./CascadingCategorySelect";

type TreeNode = CascadingCategoryItem & { children: TreeNode[] };

function buildTree(items: CascadingCategoryItem[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  for (const item of items) {
    byId.set(item.id, { ...item, children: [] });
  }
  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    if (node.parentId && byId.has(node.parentId)) {
      byId.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortRecursive = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name, "lo"));
    for (const n of nodes) sortRecursive(n.children);
  };
  sortRecursive(roots);
  return roots;
}

function filterTree(
  tree: TreeNode[],
  query: string,
): { tree: TreeNode[]; expandIds: Set<string> } {
  const q = query.trim().toLowerCase();
  if (!q) return { tree, expandIds: new Set() };
  const expandIds = new Set<string>();
  const walk = (nodes: TreeNode[]): TreeNode[] => {
    const kept: TreeNode[] = [];
    for (const n of nodes) {
      const childKept = walk(n.children);
      const selfMatch = n.name.toLowerCase().includes(q);
      if (selfMatch || childKept.length > 0) {
        if (childKept.length > 0) expandIds.add(n.id);
        kept.push({ ...n, children: childKept });
      }
    }
    return kept;
  };
  return { tree: walk(tree), expandIds };
}

function collectExpandableIds(tree: TreeNode[]): Set<string> {
  const ids = new Set<string>();
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      if (n.children.length > 0) {
        ids.add(n.id);
        walk(n.children);
      }
    }
  };
  walk(tree);
  return ids;
}

type Props = {
  categories: CascadingCategoryItem[];
  value: string;
  onChange: (categoryId: string) => void;
  disabled?: boolean;
  clearable?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  className?: string;
};

function pathLabel(
  byId: Map<string, CascadingCategoryItem>,
  id: string,
): string {
  const parts: string[] = [];
  let cur: string | null = id;
  const guard = new Set<string>();
  while (cur && byId.has(cur) && !guard.has(cur)) {
    guard.add(cur);
    parts.unshift(byId.get(cur)!.name);
    cur = byId.get(cur)!.parentId ?? null;
  }
  return parts.join(" › ");
}

function ancestorIds(
  byId: Map<string, CascadingCategoryItem>,
  id: string,
): string[] {
  const out: string[] = [];
  let cur: string | null = byId.get(id)?.parentId ?? null;
  const guard = new Set<string>();
  while (cur && byId.has(cur) && !guard.has(cur)) {
    guard.add(cur);
    out.push(cur);
    cur = byId.get(cur)!.parentId ?? null;
  }
  return out;
}

function TreeRow({
  node,
  depth,
  expanded,
  selectedId,
  onToggle,
  onSelect,
}: {
  node: TreeNode;
  depth: number;
  expanded: Set<string>;
  selectedId: string;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
}) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const isSelected = selectedId === node.id;
  const Folder = hasChildren && isOpen ? FolderOpenIcon : FolderIcon;

  return (
    <li>
      <div
        className={cn(
          "flex items-center gap-0.5 rounded-md",
          isSelected ? "bg-accent text-accent-foreground" : "hover:bg-muted/60",
        )}
        style={{ paddingLeft: `${depth * 0.85 + 0.25}rem` }}
      >
        <button
          type="button"
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40"
          aria-label={isOpen ? "ຫຍໍ້" : "ຂະຫຍາຍ"}
          disabled={!hasChildren}
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) onToggle(node.id);
          }}
        >
          {hasChildren ? (
            isOpen ? (
              <ChevronDownIcon className="size-4" />
            ) : (
              <ChevronRightIcon className="size-4" />
            )
          ) : (
            <span className="size-4" />
          )}
        </button>

        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1 py-1.5 text-left text-sm"
          onClick={() => onSelect(node.id)}
        >
          <Folder className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 truncate">{node.name}</span>
          {isSelected ? (
            <CheckIcon className="size-4 shrink-0 text-primary" aria-hidden />
          ) : null}
        </button>
      </div>

      {hasChildren && isOpen ? (
        <ul className="m-0 list-none p-0">
          {node.children.map((child) => (
            <TreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              selectedId={selectedId}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function CategoryTreePopover({
  categories,
  value,
  onChange,
  disabled,
  clearable = true,
  isLoading,
  placeholder = "ກອງຕາມໝວດ...",
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const byId = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories],
  );

  const fullTree = useMemo(() => buildTree(categories), [categories]);

  const { tree, expandIds: searchExpandIds } = useMemo(
    () => filterTree(fullTree, query),
    [fullTree, query],
  );

  useEffect(() => {
    if (!open) return;
    if (query.trim()) {
      setExpanded(new Set(searchExpandIds));
      return;
    }
    if (value && byId.has(value)) {
      setExpanded(new Set(ancestorIds(byId, value)));
      return;
    }
    setExpanded(new Set());
  }, [open, query, searchExpandIds, value, byId]);

  const selectedLabel =
    value && byId.has(value) ? pathLabel(byId, value) : "";

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelect = (id: string) => {
    onChange(id);
    setOpen(false);
    setQuery("");
  };

  const handleClear = (e?: MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    onChange("");
    setQuery("");
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn("h-8 w-full justify-between font-normal", className)}
        >
          <span className="truncate text-left">
            {isLoading
              ? "ກຳລັງໂຫຼດໝວດ..."
              : selectedLabel || placeholder}
          </span>
          <ChevronsUpDown className="ms-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(100vw-2rem,20rem)] p-0" align="start">
        <div className="border-b p-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ຄົ້ນຫາໝວດ..."
            className="h-8"
            autoFocus
          />
        </div>
        <div className="flex items-center justify-between gap-1 border-b px-2 py-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setExpanded(collectExpandableIds(tree))}
          >
            ຂະຫຍາຍທັງໝົດ
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setExpanded(new Set())}
          >
            ຫຍໍ້ທັງໝົດ
          </Button>
        </div>
        <div className="max-h-72 overflow-auto p-1">
          {categories.length === 0 ? (
            <p className="px-2 py-6 text-center text-muted-foreground text-sm">
              ຍັງບໍ່ມີໝວດສິນຄ້າ
            </p>
          ) : tree.length === 0 ? (
            <p className="px-2 py-6 text-center text-muted-foreground text-sm">
              ບໍ່ພົບໝວດທີ່ຄົ້ນຫາ
            </p>
          ) : (
            <ul className="m-0 list-none p-0">
              {tree.map((node) => (
                <TreeRow
                  key={node.id}
                  node={node}
                  depth={0}
                  expanded={expanded}
                  selectedId={value}
                  onToggle={toggle}
                  onSelect={handleSelect}
                />
              ))}
            </ul>
          )}
        </div>
        {clearable && value ? (
          <div className="border-t p-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-full justify-start text-muted-foreground"
              onClick={() => {
                handleClear();
                setOpen(false);
              }}
            >
              ລ້າງການເລືອກ
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
